<template>
  <prona-wizard-step class="wizard-step-nims-gardenInformation" :custom-proceed-function="customProceed" :errors="validationError">
    <p-request-alert v-model="error"></p-request-alert>
    <p-progress-linear v-if="isLoading" class="my-5" indeterminate></p-progress-linear>
    <div v-else>
      <div class="nims-gardenSize">
        <p-row>
          <p-col>
            <p-number-field v-model="wizardData.gardenSize" :label="$t('pronatura.widget.nimswizard.gardenInformation.gardenSize')"></p-number-field>
          </p-col>
        </p-row>
      </div>
      <div class="nims-topic-gardenType">
        <p-row>
          <p-col>
            <p>{{ $t('pronatura.widget.nimswizard.gardenInformation.gardenTypeLabel') }}</p>
            <p-radio-field
              v-for="gardenType in gardenTypes.items"
              :key="gardenType.id"
              v-model="wizardData.gardenType"
              :label="gardenType.caption"
              :true-value="gardenType.id"
              name="gardenType"
            ></p-radio-field>
          </p-col>
        </p-row>
      </div>
      <div class="nims-topic-gardenOwner">
        <p-row>
          <p-col>
            <p>{{ $t('pronatura.widget.nimswizard.gardenInformation.isGardenOwnerLabel') }}</p>
            <div :class="classList">
              <p-radio-field
                v-model="wizardData.isGardenOwner"
                :true-value="true"
                :label="$t('core.app.yes')"
                name="isGardenOwner"
                required
                @change="onIsGardenOwnerSelect(true)"
              ></p-radio-field>
              <p-radio-field
                v-model="wizardData.isGardenOwner"
                :true-value="false"
                :label="$t('core.app.no')"
                name="isGardenOwner"
                required
                @change="onIsGardenOwnerSelect(false)"
              ></p-radio-field>
            </div>
          </p-col>
        </p-row>
        <p-row v-if="wizardData.isGardenOwner === false" :class="classRegulations">
          <p-col>
            <p-checkbox v-model="wizardData.hasAcceptedRegulations" :label="$t('pronatura.widget.nimswizard.gardenInformation.hasAcceptedRegulations')" required @change="onIsGardenOwnerInformed(wizardData.hasAcceptedRegulations)"></p-checkbox>
          </p-col>
        </p-row>
      </div>
    </div>
  </prona-wizard-step>
</template>

<script lang="ts">
  import GuidLookupResourceListResultViewModel from '@/src/services/v1/viewModel/resource/GuidLookupResourceListResultViewModel';
  import NimSRegistrationRequestViewModel from '@/src/services/v1/viewModel/resource/NimSRegistrationRequestViewModel';
  import Vue, { PropType } from 'vue';

  export default Vue.extend({

    name: 'WizardStepNimSGardenInformation',
    props: {
      value: { type: Object, default: undefined },
      proceedFunction: { type: Function as PropType<(()=>void)>, default: () => {} },
    },
    data: () => ({
      isLoading: false,
      error: undefined as any,
      gardenTypes: {} as GuidLookupResourceListResultViewModel,
      isGardenOwnerError: undefined as string | undefined,
      hasAcceptedRegulationsError: undefined as string | undefined,
    }),
    computed: {
      validationError(): string {
        if (this.wizardData.isGardenOwner === undefined) {
          return this.isGardenOwnerError ?? '';
        } if (this.wizardData.isGardenOwner === false) {
          return this.hasAcceptedRegulationsError ?? '';
        }
        return '';
      },
      classList(): Record<string, boolean> {
        return {
          'input-error': this._.isSet(this.isGardenOwnerError),
        };
      },
      classRegulations(): Record<string, boolean> {
        return {
          'input-error': this._.isSet(this.hasAcceptedRegulationsError),
        };
      },
      wizardData: {
        get(): NimSRegistrationRequestViewModel {
          return this.value ?? {};
        },
        set(value: NimSRegistrationRequestViewModel) {
          this.$emit('input', value);
        },
      },
    },
    watch: {
      'wizardData.isGardenOwner': {
        handler() {
          if (this._.isSet(this.wizardData.isGardenOwner)) {
            this.isGardenOwnerError = undefined;
          }
        },
      },
      'wizardData.hasAcceptedRegulations': {
        handler() {
          if (this.wizardData.hasAcceptedRegulations === true) {
            this.hasAcceptedRegulationsError = undefined;
          } else {
            this.hasAcceptedRegulationsError = this.$t('pronatura.widget.nimswizard.gardenInformation.hasAcceptedRegulations.errorMessage');
          }
        },
      },
    },
    async beforeMount() {
      this.isLoading = true;
      try {
        this.gardenTypes = await this.$service.api.widgetNimSGardenTypes.getNimSGardenTypes();
      } catch (error: any) {
        this.error = error;
        this.$log.error(error);
      }
      this.isLoading = false;
    },
    methods: {
      validate(): boolean {
        if (!this._.isSet(this.wizardData.isGardenOwner)) {
          this.isGardenOwnerError = this.$t('pronatura.widget.nimswizard.gardenInformation.isGardenOwner.errorMessage');
          return false;
        }
        return true;
      },
      validatehasAcceptedRegulations(): boolean {
        if (!this._.isSet(this.wizardData.hasAcceptedRegulations)) {
          this.hasAcceptedRegulationsError = this.$t('pronatura.widget.nimswizard.gardenInformation.hasAcceptedRegulations.errorMessage');
          return false;
        } if (this.wizardData.hasAcceptedRegulations === false) {
          this.hasAcceptedRegulationsError = this.$t('pronatura.widget.nimswizard.gardenInformation.hasAcceptedRegulations.errorMessage');
          return false;
        }
        return true;
      },
      customProceed() {
        if (this.wizardData.isGardenOwner === false) {
          if (this.validate() && this.validatehasAcceptedRegulations()) {
            this.proceedFunction!();
          }
        } else if (this.validate()) {
          this.proceedFunction!();
        }
      },
      async onIsGardenOwnerSelect(value:boolean) {
        this.wizardData.isGardenOwner = value;
        this.$set(this.wizardData, 'isGardenOwner', value);
      },
      async onIsGardenOwnerInformed(value:boolean) {
        this.wizardData.hasAcceptedRegulations = value;
        this.$set(this.wizardData, 'hasAcceptedRegulations', value);
      },
    },
  });
</script>
